import { Menu } from '@headlessui/react';
import { pipe, constNull } from 'fp-ts/lib/function';
import {
  ArrowLeft,
  Home as HomeIcon,
  BarChart2,
  Shield,
  IconProps,
} from 'react-feather';
import * as React from 'react';
import * as O from 'fp-ts/Option';
import * as A from 'fp-ts/Array';
import { useHistory, useLocation } from 'react-router';
import { Link, NavLinkProps } from 'react-router-dom';

import { ButtonLink } from '../components/Button';
import { InternalLink, usePreviousPathname } from '../components/InternalLink';
import { useAuth } from '../contexts/AuthContext';
import { FullTeamFragment, Team, FullUserFragment } from '../generated/graphql';
import { paths } from '../paths';
import { useTeam } from '../contexts/TeamContext';

import { isNil } from '../utils/isNil';
import { Logo } from '../icons/Logo';
import { withIntendedPath } from '../utils/withIntendedPath';
import { HeaderMenu } from './HeaderMenu';

export type SelectedTeam = Pick<Team, 'id' | 'isOwner'> &
  Partial<Pick<FullTeamFragment, 'requests'>>;

interface Props {
  header?: React.ReactNode;
}

const rootPages = [paths.home()];

const HeaderBar: React.FC = ({}) => {
  const previousPath = usePreviousPathname();
  const h = useHistory();
  const l = useLocation();
  const hasPrevious =
    previousPath !== paths.home() && !rootPages.some((p) => p === l.pathname);

  return (
    <Menu as={React.Fragment}>
      {({ open }) => (
        <div
          className={`fixed z-20 inset-x-0 top-0 bg-primary ${
            open ? '' : 'backdrop-filter backdrop-blur bg-opacity-80'
          }`}
        >
          <div className="flex justify-between py-3 h-14 max-w-5xl mx-auto px-6 mt-safe-top text-white">
            {hasPrevious ? (
              <button onClick={() => h.goBack()} type="button" title="Go back">
                <ArrowLeft />
              </button>
            ) : (
              <Link to={paths.home()} title="Go to Home">
                <Logo height="100%" />
              </Link>
            )}

            <div className="relative flex items-center text-white top-0">
              <HeaderMenu open={open} />
            </div>
          </div>
        </div>
      )}
    </Menu>
  );
};

interface NavItem {
  Icon: React.FC<IconProps>;
  label: string;
  to: string;
}

const getNavItems =
  (user: Pick<FullUserFragment, 'settings' | 'teams'> | null) =>
  (team: FullTeamFragment): NavItem[] => {
    const baseNavItems: NavItem[] = [
      { Icon: HomeIcon, label: 'Home', to: paths.home() },
    ];

    const leaderboardNavItem = pipe(
      user?.teams,
      O.fromNullable,
      O.filter(A.isNonEmpty),
      O.map((): NavItem[] => [
        {
          Icon: BarChart2,
          label: 'Stats',
          to: paths.stats.home(),
        },
      ]),
      O.getOrElse((): NavItem[] => []),
    );

    const clubNavItem: NavItem[] = !isNil(team.club)
      ? [{ Icon: Shield, label: 'Club', to: paths.clubs.home(team.club.id) }]
      : team.isOwner
      ? [
          {
            Icon: Shield,
            label: 'Club',
            to: paths.teamSettings.club.clubIntro(),
          },
        ]
      : [];

    return pipe(
      baseNavItems,
      A.concat(leaderboardNavItem),
      A.concat(clubNavItem),
    );
  };

const navLinkIsActive =
  (dest: string): NavLinkProps['isActive'] =>
  (_, location) => {
    return dest === '/'
      ? dest === location.pathname
      : location.pathname.startsWith(dest);
  };

const NavLinkItem: React.FC<{ item: NavItem }> = ({ item }) => {
  return (
    <li>
      <InternalLink
        className="hover:text-primary-500 rounded-full inline-block sm:px-4"
        to={item.to}
        title={item.label}
        activeClassName="text-primary-500"
        exact={true}
        isActive={navLinkIsActive(item.to)}
      >
        <span className="flex items-center flex-col sm:flex-row">
          <item.Icon className="block sm:mr-4 h-5 w-5 sm:w-auto sm:h-auto " />
          <span className="block">{item.label}</span>
        </span>
      </InternalLink>
    </li>
  );
};

export const MainLayout: React.FC<Props> = ({ children, header }) => {
  const { user } = useAuth();
  const { team } = useTeam();
  const { pathname, search } = useLocation();

  const navItems = pipe(
    team,
    O.map(getNavItems(user)),
    O.map((items) =>
      items.map((item) => <NavLinkItem key={item.label} item={item} />),
    ),
  );

  return (
    <div
      className={`bg-white min-h-screen disable-dbl-tap-zoom ${
        isNil(user) ? '' : 'pb-12 sm:pb-0'
      }`}
    >
      <div
        className="mb-6 bg-primary"
        style={{
          paddingTop: 'calc(3.5rem + env(safe-area-inset-top)',
        }}
      >
        <HeaderBar />
      </div>
      {!isNil(header) ? header : null}

      {/* sidebar menu for larger screens */}
      <div className="sm:grid grid-cols-8 gap-x-4 max-w-5xl mx-auto sm:mb-0 py-6 px-6">
        <aside className="hidden sm:block col-span-2">
          {!isNil(user) ? (
            pipe(
              navItems,
              O.fold(constNull, (items) => (
                <nav>
                  <ul className="space-y-3 font-semibold text-xl text-grey-800">
                    {items}
                  </ul>
                </nav>
              )),
            )
          ) : (
            <div className="space-y-4">
              <p className="text-sm">
                Keep up to speed with your team and get access to stats and more
              </p>
              <ButtonLink
                to={withIntendedPath(paths.signIn(), `${pathname}${search}`)}
                kind="plain"
              >
                Log in
              </ButtonLink>
              <ButtonLink to={paths.register()}>Sign up</ButtonLink>
            </div>
          )}
        </aside>

        <div className="text-grey-800 pb-safe-bottom col-span-6">
          {children}
        </div>
      </div>

      {/* footer menu for mobile */}
      {pipe(
        navItems,
        O.filter(() => !isNil(user)),
        O.fold(constNull, (items) => (
          <nav className="w-full border-t border-grey-100 fixed bottom-0 bg-white sm:hidden py-2 z-10">
            <ul className="flex items-center justify-around h-full text-grey-700 pb-safe-bottom text-2xs">
              {items}
            </ul>
          </nav>
        )),
      )}
    </div>
  );
};
